import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import type { BaseProperty, Group, Property } from '@app/shared/models/common-models'
import { PropertyService } from '@app/shared/services/property.service'
import { EntityService } from '@app/shared/services/entity.service'

export interface ResultGroupProperty extends BaseProperty {
  IsNumeric?: boolean
  MinValue?: number
  MaxValue?: number
  TooltipTextId?: string
}

@Component({
  selector: 'app-result-group',
  templateUrl: './result-group.component.html',
  styleUrls: ['./result-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResultGroupComponent implements OnChanges {
  @Input() group: Group

  @Input() textIdOverrides: Record<string, string>

  dataSource: ResultGroupProperty[]

  visibleColumns: string[]

  isMinMax = false

  hasMaxValue = false

  hasMinValue = false

  valueDefinition = 'value'

  constructor(
    private propertyService: PropertyService,
    private entityService: EntityService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.group && this.group) {
      this.dataSource = this.createDataSource(this.group)
      this.visibleColumns = ['text', 'value']
    }

    if (changes.group && this.group && this.checkMinMax(this.group.Properties)) {
      this.isMinMax = true
      this.dataSource = this.createMinMaxData(this.group)
      this.hasMaxValue = this.dataSource.some(property => property.MaxValue)
      this.hasMinValue = this.dataSource.some(property => property.MinValue)
      this.valueDefinition = 'normal'
      this.visibleColumns = ['text', 'normal', 'minValue', 'maxValue']
    }
  }

  createDataSource = (group: Group): ResultGroupProperty[] => {
    return group.Properties.map(p => {
      const property: ResultGroupProperty = {
        Key: p.Key,
        Value: p.Value,
        TextId: p.TextId,
        TitleSuffix: p.TitleSuffix,
        MeasurementUnitTextId: p.MeasurementUnitTextId,
        IsNumeric: false,
        IsSubProperty: p.IsSubProperty,
        ValueType: p.ValueType,
        Metadata: p.Metadata,
        Decimals: p.Decimals,
        TooltipTextId: this.entityService.getMetadataValue(p, 'TooltipTextId'),
      }

      if (!Number.isNaN(Number(property.Value))) {
        property.IsNumeric = true
        return property
      }

      if (typeof property.Value !== 'string' || (!property.Value.includes('{') && !property.Value.includes('}'))) {
        return property
      }

      property.Value = this.propertyService.injectTranslatedValuesToPropertyValue(property)

      return property
    })
  }

  private checkMinMax = (properties): boolean => {
    return properties.some(property =>
      property.Metadata.some(m => {
        return m.Value === 'Min' || m.Value === 'Max'
      }),
    )
  }

  private createMinMaxData = (group: Group): Property[] => {
    // Filter out the min and max values
    let properties = group.Properties.filter(p => p.Metadata.every(m => m.Value !== 'Min' && m.Value !== 'Max'))
    // Get the min and max value
    const minMetadataProperties = group.Properties.filter(p => p.Metadata.some(m => m.Value === 'Min'))
    const maxMetadataProperties = group.Properties.filter(p => p.Metadata.some(m => m.Value === 'Max'))
    properties = properties.map(p => {
      const property = { ...p } // Combine the normal, min and max value, based on the Key
      const minProperty = minMetadataProperties.find(mp => mp.Metadata.some(m => m.Key === p.Key))
      const maxProperty = maxMetadataProperties.find(mp => mp.Metadata.some(m => m.Key === p.Key))
      property.MinValue = minProperty ? Number(minProperty.Value) : null
      property.MaxValue = maxProperty ? Number(maxProperty.Value) : null
      return property
    })
    return properties
  }
}
